/**
 * Supported Locales Configuration (SSOT)
 *
 * Single Source of Truth for all supported languages in the application.
 * When adding a new language:
 * 1. Add entry here
 * 2. Create .po file in languages/
 * 3. Run npm run i18n:sync
 *
 * @package Datatime\DDD
 * @since 0.58.0
 */

import { sk, cs, enUS, de, pl, hu, es } from 'date-fns/locale';
import type { Locale } from 'date-fns';

export interface LocaleConfig {
	/** Display name in native language */
	name: string;
	/** Flag emoji */
	flag: string;
	/** date-fns locale for date formatting */
	dateFns: Locale;
	/** Intl locale string for number formatting (e.g., 'sk-SK') */
	intl: string;
	/** Whether this locale is bundled inline (no JSON file needed) */
	bundled?: boolean;
}

/**
 * All supported locales in the application.
 *
 * To add a new language:
 * 1. Import date-fns locale at the top
 * 2. Add entry to this object
 * 3. Create .po file: languages/datatime-ddd-{code}_{CODE}.po
 * 4. Run: npm run i18n:sync
 *
 * The i18n sync script will automatically:
 * - Convert .po to .json for React
 * - Generate .mo for PHP gettext
 * - Generate .l10n.php for WordPress optimized translations
 * - Create locales/{code}/ directory if needed
 */
export const SUPPORTED_LOCALES: Record<string, LocaleConfig> = {
	sk: {
		name: 'Slovenčina',
		flag: '🇸🇰',
		dateFns: sk,
		intl: 'sk-SK',
		bundled: true, // Slovak is the source language, no translation file needed
	},
	cs: {
		name: 'Čeština',
		flag: '🇨🇿',
		dateFns: cs,
		intl: 'cs-CZ',
	},
	en: {
		name: 'English',
		flag: '🇬🇧',
		dateFns: enUS,
		intl: 'en-US',
	},
	de: {
		name: 'Deutsch',
		flag: '🇩🇪',
		dateFns: de,
		intl: 'de-DE',
	},
	pl: {
		name: 'Polski',
		flag: '🇵🇱',
		dateFns: pl,
		intl: 'pl-PL',
	},
	hu: {
		name: 'Magyar',
		flag: '🇭🇺',
		dateFns: hu,
		intl: 'hu-HU',
	},
	es: {
		name: 'Español',
		flag: '🇪🇸',
		dateFns: es,
		intl: 'es-ES',
	},
} as const;

/** Short locale codes (e.g., 'sk', 'cs', 'en') */
export type LocaleCode = keyof typeof SUPPORTED_LOCALES;

/** Array of all supported locale codes */
export const SUPPORTED_LOCALE_CODES = Object.keys(SUPPORTED_LOCALES) as LocaleCode[];

/** Default/fallback locale */
export const DEFAULT_LOCALE: LocaleCode = 'sk';

/**
 * Check if a locale code is supported
 */
export function isValidLocale(code: string): code is LocaleCode {
	return code in SUPPORTED_LOCALES;
}

/**
 * Get locale config, with fallback to default
 */
export function getLocaleConfig(code: string): LocaleConfig {
	return SUPPORTED_LOCALES[code] || SUPPORTED_LOCALES[DEFAULT_LOCALE];
}
